import React, { useState, useEffect, useContext } from "react";

import { Error, Info } from "icons";
import { format } from "sql-formatter";

import ingestionDataApi from "apis/ingestionData";
import sqlEditorHistoryApi from "apis/sqlEditorHistory";
import { Callout, PageLoader, Tooltip } from "clearsense-ui/components";
import { EmptyState } from "components/Common";
import ThemeContext from "contexts/ThemeContext";
import useAutoCollapse from "hooks/useAutoCollapse";

import CreateViewModal from "./CreateViewModal";
import DBPreview from "./DBPreview";
import Editor from "./Editor";
import PreviewTable from "./PreviewTable";
import ResultTable from "./ResultTable";
import SQLHistoryCard from "./SQLHistoryCard";
import Tabs from "./Tabs";

const SQLEditor = () => {
  const [currentTab, setCurrentTab] = useState("results");
  const [result, setResult] = useState([]);
  const [sqlError, setSqlError] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [sqlQuery, setSqlQuery] = useState("");
  const [history, setHistory] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isExecutingSQL, setIsExecutingSQL] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [previewTables, setPreviewTables] = useState({});
  const [showCreateViewModal, setShowCreateViewModal] = useState(false);

  const { theme } = useContext(ThemeContext);

  const fetchHistory = async () => {
    try {
      const { data } = await sqlEditorHistoryApi.fetchHistory();
      setHistory(data.history);
    } catch (err) {
      logger.log(err);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const executeSQL = async (pageNumber, pageSize, saveHistory, addOn = "") => {
    setCurrentTab("results");
    setSqlError("");
    setIsFetching(true);
    try {
      const sqlQueryToExecute = format(addOn + sqlQuery, { uppercase: true });

      const res = await ingestionDataApi.executeSQL(
        sqlQueryToExecute,
        pageNumber,
        pageSize,
        saveHistory
      );
      res.data.errors && setSqlError(res.data.errors);
      sqlQueryToExecute.toUpperCase().includes("CREATE VIEW") ||
      sqlQueryToExecute.toUpperCase().includes("CREATE MATERIALIZED VIEW")
        ? setResult(sqlQueryToExecute.split(/ view /gi)[1].split(" ")[0])
        : setResult(res.data.result);
      setTotalRecords(res.data.length);
      fetchHistory();
    } catch (err) {
      logger.log(err);
    } finally {
      setIsFetching(false);
      setIsExecutingSQL(false);
      setShowCreateViewModal(false);
    }
  };

  useEffect(() => {
    sqlQuery.length !== 0 && executeSQL(pageNumber, pageSize, false);
  }, [pageNumber, pageSize]);

  const onExecuteButtonClick = (addOn = "") => {
    setSqlQuery(format(`${sqlQuery.split(";")[0]};`, { uppercase: true }));
    setIsExecutingSQL(true);
    setPageNumber(1);
    setPageSize(100);
    executeSQL(1, 100, true, addOn);
  };

  useAutoCollapse();

  return (
    <>
      <DBPreview
        selectedColumns={selectedColumns}
        previewTables={previewTables}
        currentTab={currentTab}
        setSelectedColumns={setSelectedColumns}
        setPreviewTables={setPreviewTables}
        setCurrentTab={setCurrentTab}
      />
      <div className="flex flex-col ml-80">
        <Editor
          sqlQuery={sqlQuery}
          isFetching={isFetching}
          isExecutingSQL={isExecutingSQL}
          setSqlQuery={setSqlQuery}
          setShowCreateViewModal={setShowCreateViewModal}
          onExecuteButtonClick={onExecuteButtonClick}
          theme={theme}
        />
        <Tabs
          currentTab={currentTab}
          previewTables={previewTables}
          setCurrentTab={setCurrentTab}
        />
        <div className="px-4" style={{ height: "calc(100vh - 460px)" }}>
          {currentTab === "results" && (
            <>
              {isFetching && <PageLoader />}
              {!sqlError &&
                !result?.length &&
                typeof result !== "string" &&
                !isFetching && <EmptyState mainText="No Results" />}
              {!sqlError && typeof result === "string" && !isFetching && (
                <Callout
                  data-cy="sql-editor-view-created-callout"
                  size="large"
                  className="text-xl m-8"
                  icon={Info}
                >
                  {`View [${result}] was created`}
                </Callout>
              )}
              {!isFetching &&
                !sqlError &&
                typeof result !== "string" &&
                !!result?.length && (
                  <ResultTable
                    result={result}
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    setPageNumber={setPageNumber}
                    setPageSize={setPageSize}
                  />
                )}
              {sqlError && (
                <Callout
                  size="large"
                  style="danger"
                  className="text-xl m-8"
                  icon={Error}
                  data-cy="sql-editor-error-callout"
                >
                  {sqlError}
                </Callout>
              )}
            </>
          )}

          {currentTab === "sqlHistory" && (
            <>
              {!history.length && <EmptyState mainText="No History" />}
              {!!history.length && (
                <div
                  data-cy="sql-editor-query-history-container"
                  className="flex flex-col space-y-4 py-4 overflow-y-auto h-full cs-ui-scrollbar"
                >
                  {history.map(item => (
                    <Tooltip
                      key={item.id}
                      position="top"
                      content="Click to copy query to Editor"
                      followCursor="auto"
                    >
                      <SQLHistoryCard
                        item={item}
                        setSqlQuery={setSqlQuery}
                        theme={theme}
                      />
                    </Tooltip>
                  ))}
                </div>
              )}
            </>
          )}
          {currentTab !== "results" && currentTab !== "sqlHistory" && (
            <PreviewTable
              previewTables={previewTables}
              currentTab={currentTab}
              selectedColumns={selectedColumns}
            />
          )}
          <CreateViewModal
            showCreateViewModal={showCreateViewModal}
            isFetching={isFetching}
            setShowCreateViewModal={setShowCreateViewModal}
            onExecuteButtonClick={onExecuteButtonClick}
          />
        </div>
      </div>
    </>
  );
};

export default SQLEditor;
